import {
  DEFAULT_FRAME_SIZE,
  DEFAULT_NOTEBOOK_SIZE,
  DEFAULT_SHAPE_SIZE,
  STICKY_SIZE,
  createFrame,
  createNotebook,
  createShape,
  createSticky,
  createText,
  rectFromPoints,
  snapPointToGrid,
  type BoardNode,
  type Rect,
} from '@kanvas/board-core';
import { reparentIntoFrames } from '../../actions/actions';
import { invalidate, scratch } from '../../state/scratch';
import { useEditor } from '../../state/store';
import type { Gesture, GestureContext } from '../types';

export type CreateKind = 'shape' | 'frame' | 'sticky' | 'text' | 'notebook';

/** Drag shorter than this (board units) counts as a click, not a sizing drag. */
const CLICK_SLOP = 5;

/**
 * Drag-to-create.
 *
 * A click creates the object at its default size centred on the pointer; a drag
 * sizes it. Sticky notes are the exception: they keep a fixed size on click,
 * because a sticky whose size varies per click looks wrong in a cluster.
 */
export function createBoxGesture(ctx: GestureContext, kind: CreateKind): Gesture {
  const doc = ctx.doc;
  const meta = doc.getMeta();
  const origin = meta.snapToGrid
    ? snapPointToGrid(ctx.pointer.board, meta.gridSize)
    : ctx.pointer.board;

  let dragged = false;

  return {
    cursor: 'crosshair',
    move(next) {
      const to = meta.snapToGrid
        ? snapPointToGrid(next.pointer.board, meta.gridSize)
        : next.pointer.board;
      let area = rectFromPoints(origin, to);

      if (next.mods.shift) {
        // Constrain to a square, anchored at the origin corner.
        const edge = Math.max(area.w, area.h);
        area = {
          x: to.x < origin.x ? origin.x - edge : origin.x,
          y: to.y < origin.y ? origin.y - edge : origin.y,
          w: edge,
          h: edge,
        };
      }

      dragged = area.w > CLICK_SLOP || area.h > CLICK_SLOP;
      scratch.creationPreview = { ...area, kind };
      scratch.gestureActive = true;
      invalidate();
    },

    up(next) {
      scratch.creationPreview = null;
      scratch.gestureActive = false;

      const to = meta.snapToGrid
        ? snapPointToGrid(next.pointer.board, meta.gridSize)
        : next.pointer.board;
      let area = rectFromPoints(origin, to);
      if (next.mods.shift && dragged) {
        const edge = Math.max(area.w, area.h);
        area = {
          x: to.x < origin.x ? origin.x - edge : origin.x,
          y: to.y < origin.y ? origin.y - edge : origin.y,
          w: edge,
          h: edge,
        };
      }

      const node = buildNode(ctx, kind, dragged ? area : null, origin);
      if (!node) {
        invalidate();
        return;
      }

      doc.add(node);
      // A new frame adopts whatever it now covers (photos by centre, everything
      // else only when fully inside). Other tools nest the object they just made.
      const adopt =
        node.type === 'frame'
          ? doc.all().filter((n) => n.id !== node.id).map((n) => n.id)
          : [node.id];
      reparentIntoFrames({ doc, identity: next.identity }, adopt);
      doc.breakUndoBatch();

      const editor = useEditor.getState();
      // Order matters: setTool clears the inline editor, so the tool switch has
      // to happen before editing starts, not after.
      if (!editor.toolLocked) editor.setTool('select');
      editor.select([node.id]);
      // Text and sticky notes are created to be typed into, so drop straight
      // into the inline editor.
      if (kind === 'sticky' || kind === 'text') editor.setEditing(node.id);
      invalidate();
    },

    cancel() {
      scratch.creationPreview = null;
      scratch.gestureActive = false;
      invalidate();
    },
  };
}

function buildNode(
  ctx: GestureContext,
  kind: CreateKind,
  area: Rect | null,
  origin: { x: number; y: number },
): BoardNode | null {
  const doc = ctx.doc;
  const editor = useEditor.getState();
  const factoryCtx = { userId: ctx.identity.userId, index: doc.topIndex() };

  switch (kind) {
    case 'shape': {
      const box = area ?? {
        x: origin.x - DEFAULT_SHAPE_SIZE.w / 2,
        y: origin.y - DEFAULT_SHAPE_SIZE.h / 2,
        w: DEFAULT_SHAPE_SIZE.w,
        h: DEFAULT_SHAPE_SIZE.h,
      };
      return createShape(editor.shapeKind, box.x, box.y, factoryCtx, {
        w: box.w,
        h: box.h,
        style: { ...editor.shapeStyle },
        textStyle: { ...editor.textStyle },
      });
    }
    case 'sticky': {
      const box = area && area.w > STICKY_SIZE / 2
        ? area
        : { x: origin.x - STICKY_SIZE / 2, y: origin.y - STICKY_SIZE / 2, w: STICKY_SIZE, h: STICKY_SIZE };
      return createSticky(box.x, box.y, factoryCtx, {
        w: box.w,
        h: box.h,
        fill: editor.stickyFill,
      });
    }
    case 'text': {
      const box = area ?? { x: origin.x, y: origin.y - 16, w: 280, h: 36 };
      return createText(box.x, box.y, factoryCtx, {
        w: box.w,
        h: area ? box.h : 36,
        autoHeight: !area,
        textStyle: { ...useEditor.getState().textStyle, align: 'left', valign: 'top' },
      });
    }
    case 'frame': {
      const box = area ?? {
        x: origin.x - DEFAULT_FRAME_SIZE.w / 2,
        y: origin.y - DEFAULT_FRAME_SIZE.h / 2,
        w: DEFAULT_FRAME_SIZE.w,
        h: DEFAULT_FRAME_SIZE.h,
      };
      const count = doc.all().filter((n) => n.type === 'frame').length;
      return createFrame(box.x, box.y, factoryCtx, {
        w: box.w,
        h: box.h,
        name: `Фрейм ${count + 1}`,
      });
    }
    case 'notebook': {
      const count = doc.all().filter((n) => n.type === 'notebook').length;
      return createNotebook(
        origin.x - DEFAULT_NOTEBOOK_SIZE.w / 2,
        origin.y - DEFAULT_NOTEBOOK_SIZE.h / 2,
        factoryCtx,
        {
          title: `Тетрадь ${count + 1}`,
          paper: 'grid',
        },
      );
    }
  }
}
